import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UtensilsCrossed,
  Coffee,
  Loader2,
  ArrowRight,
  Store,
  Settings,
  LogOut,
  Star
} from 'lucide-react';
import Header from '../../components/shared/Header';
import branchService from '../../services/branchService';
import { useAuth } from '../../context/AuthContext';
import { COUNTER_TABLE_NAME, COUNTER_DEFAULTS } from '../../config/counterConfig';

/**
 * POS Landing Page - Service Mode Selector
 *
 * Clean, intuitive design for selecting between Dine-In and Counter modes.
 * Auto-redirects if only one mode is enabled.
 */

export default function POSLandingPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [loading, setLoading] = useState(true);
  const [branch, setBranch] = useState(null);
  const [serviceModes, setServiceModes] = useState([]);
  const [defaultMode, setDefaultMode] = useState('DINE_IN');

  // Load branch settings and service modes
  useEffect(() => {
    const loadBranchSettings = async () => {
      try {
        const branchData = await branchService.getCurrentBranch();
        setBranch(branchData);

        // Parse service modes
        let modes = ['DINE_IN']; // Default fallback
        if (branchData?.serviceModes) {
          if (Array.isArray(branchData.serviceModes)) {
            modes = branchData.serviceModes;
          } else if (typeof branchData.serviceModes === 'string') {
            modes = branchData.serviceModes.split(',').map(m => m.trim()).filter(Boolean);
          }
        }

        // Ensure at least one mode
        if (modes.length === 0) {
          modes = ['DINE_IN'];
        }

        setServiceModes(modes);
        setDefaultMode(branchData?.defaultServiceMode || modes[0]);

        // Auto-redirect if only one mode is enabled
        if (modes.length === 1) {
          redirectToMode(modes[0]);
          return; // Don't set loading to false - we're navigating away
        }
      } catch (error) {
        console.error('Failed to load branch settings:', error);
        // Fallback to Dine-In if error
        setServiceModes(['DINE_IN']);
        setDefaultMode('DINE_IN');
      } finally {
        setLoading(false);
      }
    };

    loadBranchSettings();
  }, []);

  // Redirect to specific mode
  const redirectToMode = (mode) => {
    switch (mode) {
      case 'DINE_IN':
        navigate('/pos/tables');
        break;
      case 'COUNTER':
        navigate('/pos/billing/counter', {
          state: {
            tableId: 'counter',
            tableName: COUNTER_TABLE_NAME,
            split: COUNTER_DEFAULTS.split,
            isCounterMode: true,
          }
        });
        break;
      default:
        navigate('/pos/tables');
    }
  };

  // Mode configurations
  const modeConfig = {
    DINE_IN: {
      title: 'Dine-In',
      subtitle: 'Table Service',
      description: 'Select tables, manage splits, send KOT to kitchen',
      icon: UtensilsCrossed,
      iconBg: 'bg-teal-100',
      iconColor: 'text-teal-600',
    },
    COUNTER: {
      title: 'Counter',
      subtitle: 'Quick Service',
      description: 'Fast billing for walk-in and takeaway orders',
      icon: Coffee,
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
    },
  };

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin text-teal-600 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <Header />

      {/* Main Content - Centered */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-xl">
          {/* Title Section */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-full text-sm font-medium text-slate-600 shadow-sm mb-5">
              <Store size={16} className="text-teal-600" />
              {branch?.name || 'Restaurant'}
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-1">
              Select Service Mode
            </h1>
            <p className="text-slate-500">
              Choose how you want to take orders
            </p>
          </div>

          {/* Mode Selection Cards */}
          <div className="space-y-4 mb-8">
            {serviceModes.map((mode) => {
              const config = modeConfig[mode];
              if (!config) return null;
              const Icon = config.icon;
              const isDefault = mode === defaultMode;

              return (
                <button
                  key={mode}
                  onClick={() => redirectToMode(mode)}
                  className={`
                    relative w-full p-5 rounded-xl border-2 bg-white
                    transition-all duration-200 transform hover:scale-[1.01]
                    ${isDefault
                      ? 'border-teal-500 shadow-lg shadow-teal-500/10'
                      : 'border-slate-200 hover:border-slate-300 shadow-sm hover:shadow-md'
                    }
                    group
                  `}
                >
                  {/* Default Badge */}
                  {isDefault && (
                    <div className="absolute -top-2.5 left-6 flex items-center gap-1 px-2.5 py-0.5 bg-teal-500 text-white text-xs font-bold rounded-full">
                      <Star size={10} fill="currentColor" />
                      DEFAULT
                    </div>
                  )}

                  <div className="flex items-center gap-4">
                    {/* Icon */}
                    <div className={`w-14 h-14 rounded-xl ${config.iconBg} flex items-center justify-center flex-shrink-0`}>
                      <Icon size={28} className={config.iconColor} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <h3 className="text-lg font-bold text-slate-900">{config.title}</h3>
                        <span className="text-xs font-medium text-slate-400 bg-slate-100 px-2 py-0.5 rounded">
                          {config.subtitle}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500">
                        {config.description}
                      </p>
                    </div>

                    {/* Arrow */}
                    <div className="w-9 h-9 rounded-full bg-slate-100 group-hover:bg-teal-500 group-hover:text-white text-slate-400 flex items-center justify-center flex-shrink-0 transition-colors">
                      <ArrowRight size={18} />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Quick Actions */}
          <div className="flex items-center justify-center gap-6 text-sm">
            <button
              onClick={() => navigate('/admin/settings')}
              className="flex items-center gap-2 text-slate-500 hover:text-slate-700 transition-colors"
            >
              <Settings size={16} />
              Settings
            </button>
            <div className="w-px h-4 bg-slate-300"></div>
            <button
              onClick={logout}
              className="flex items-center gap-2 text-slate-500 hover:text-red-600 transition-colors"
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>

          {/* User Info */}
          <div className="text-center mt-6 text-xs text-slate-400">
            Logged in as <span className="font-medium text-slate-500">{user?.name || 'Staff'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
